
export type DayOfWeek = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
export const ALL_DAYS: DayOfWeek[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_INDEX_MAP: { [key in DayOfWeek]: number } = {
    'Sunday': 0,
    'Monday': 1,
    'Tuesday': 2,
    'Wednesday': 3,
    'Thursday': 4,
    'Friday': 5,
    'Saturday': 6
};

export type AvailabilityStatus = 'Available' | 'Preferred' | 'Unavailable';
export type AssignmentStatus = 'ASSIGNED' | 'OFF' | 'SICK' | 'VACATION' | 'ON_CALL' | 'UNASSIGNED';
export const ALL_ASSIGNMENT_STATUSES: AssignmentStatus[] = ['ASSIGNED', 'OFF', 'SICK', 'VACATION', 'ON_CALL', 'UNASSIGNED'];
export type TimeOffRequestType = 'DIRECT' | 'REPLACED';
export type TimeOffRequestStatus = 'PENDING_ADMIN' | 'PENDING_REPLACER' | 'APPROVED' | 'REJECTED';
export type Theme = 'light' | 'dark' | 'system';

export interface Department {
    id: string;
    name: string;
    color: string;
}

export interface StaffMember {
    id: string;
    name: string;
    color: string;
    email: string;
    phone: string;
    role: string;
    notes: string;
    departmentId: string | null;
    isDriver?: boolean;
    password?: string;
    status?: 'active' | 'inactive' | 'deleted';
    availability: {
        [key in DayOfWeek]: AvailabilityStatus;
    };
    availabilityOverrides?: {
        [date: string]: AvailabilityStatus; // Key is 'YYYY-MM-DD'
    };
}

export interface Shift {
    id: string;
    name:string;
    startTime: string; // "HH:mm" format
    endTime: string;   // "HH:mm" format
    color: string;
}

export interface PickupLocation {
    id: string;
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    color: string;
}

export interface ReservationSource {
    id: string;
    name: string;
}

export interface ShuttleReservation {
    id: string;
    locationId: string;
    direction: 'TO_HOTEL' | 'FROM_HOTEL';
    time: string;
    date: string; // YYYY-MM-DD
    guests: number;
    roomNumber: string;
    createdAt: string;
    status?: 'pending' | 'done';
}

export interface ScheduleEntry {
    staffId: string;
    shiftId: string | null;
    status: AssignmentStatus;
    isForced?: boolean;
}

export interface Schedule {
    [staffId: string]: {
        [date: string]: ScheduleEntry; // Key is 'YYYY-MM-DD'
    };
}

export interface ParkingReservation {
    id: string;
    reservationSource: string;
    confirmationNumber: string;
    numberOfPeople: string;
    authorizedBy: string;
    licensePlate: string;
    guestName: string;
    roomNumber: string;
    startDate: string; // YYYY-MM-DD
    endDate: string; // YYYY-MM-DD
    status: 'Reservation' | 'Check-in' | 'Checkout';
    notes?: string;
    createdAt: string;
}

export type AppPage = 'scheduler' | 'staffList' | 'settings' | 'myProfile' | 'timeOff' | 'workforce' | 'shuttle' | 'parking' | 'reports' | 'help';
export const ALL_PAGES: AppPage[] = ['scheduler', 'staffList', 'timeOff', 'shuttle', 'parking', 'workforce', 'reports', 'settings', 'myProfile', 'help'];

export interface WeeklyMetrics {
    [date: string]: {
        arrivals: number;
        departures: number;
        stayOvers: number;
        occupancy?: number;
    };
}

export interface View {
    page: AppPage;
    staffId?: string;
}

export interface LoginCredentials {
    email: string;
    password: string;
    name?: string;
}

export interface AdminUser {
    uid: string;
    email: string;
    status: 'active' | 'suspended';
    // Profile fields
    name: string;
    color: string;
    phone: string;
    role: string;
    notes: string;
    departmentId: string | null;
    isDriver?: boolean;
    availability: {
        [key in DayOfWeek]: AvailabilityStatus;
    };
    availabilityOverrides?: {
        [date: string]: AvailabilityStatus; // Key is 'YYYY-MM-DD'
    };
    showInScheduler: boolean;
    allowedPages?: AppPage[];
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  level: "info" | "warning" | "error" | "success";
  scheduledAt: string; // ISO string ONLY
  read: boolean;
}

export interface TimeOffRequest {
    id: string;
    employeeId: string;
    staffName: string;
    startDate: string; // YYYY-MM-DD
    endDate: string; // YYYY-MM-DD
    reason: string;
    status: TimeOffRequestStatus;
    createdAt: string; // ISO String
    type: TimeOffRequestType;
    replacementEmployeeId: string | null;
}